/**
 * Redux Store Configuration
 * 
 * Purpose: Centralized state management with persistence
 * Features:
 * - Redux Toolkit for simplified Redux
 * - RTK Query for API calls
 * - Redux Persist for state persistence
 * - Type-safe store
 */

import { configureStore } from '@reduxjs/toolkit'
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { rootReducer } from './rootReducer'
import { authApi } from '@/data/auth/api/auth_api'
import { adminApi } from '@/data/admin/api/admin_api'
import { dashboardApi } from '@/data/dashboard/api/dashboard_api'
import { adminPortalApi } from '@/data/admin/api/admin_portal_api'

// Persist configuration
const persistConfig = {
  key: 'root',
  storage,
  // Only persist non-sensitive data
  whitelist: ['ui', 'auth'], // Only persist UI preferences and auth (token handled separately)
  // Don't persist sensitive data in Redux - use secure storage instead
  blacklist: [],
  // Transform to exclude isLoading from persistence
  transforms: [],
}

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer)

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(authApi.middleware)
      .concat(adminApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(adminPortalApi.middleware),
  devTools: import.meta.env.DEV,
})

export const persistor = persistStore(store)

// Type exports for TypeScript
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

